'use client';
import React, { useEffect, useState, useCallback, Suspense } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import AppLayout from '@/components/AppLayout';
import ProfileHeader from './components/ProfileHeader';
import BodyMetricsBento from './components/BodyMetricsBento';
import DailyMacros from './components/DailyMacros';
import ForgeScorePanel from './components/ForgeScorePanel';
import WeightTrendChart from './components/WeightTrendChart';
import ActivityMetrics from './components/ActivityMetrics';
import GoalOnboarding from '@/components/GoalOnboarding';
import BadgeCelebration from '@/components/BadgeCelebration';
import { forgeGoalsService, ForgeGoal, ForgeBadge } from '@/lib/services/forgeGoalsService';
import { createClient } from '@/lib/supabase/client';
import { isSetupComplete, getProfileSetup, resetGoalCycle } from '@/app/components/HeroSetupForm';

function ProfileDashboardInner() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const profileParam = (searchParams?.get('profile') || 'theo') as 'theo' | 'tracy';

  const [userId, setUserId] = useState<string | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(true);
  const [unseenBadge, setUnseenBadge] = useState<ForgeBadge | null>(null);
  const [activeGoals, setActiveGoals] = useState<ForgeGoal[]>([]);
  const [profileName, setProfileName] = useState('');
  const [heroSetupMissing, setHeroSetupMissing] = useState(false);

  const checkGoalsSetup = useCallback(async (uid: string) => {
    // Gate: hero setup must be complete first
    if (!isSetupComplete(profileParam)) {
      setHeroSetupMissing(true);
      setCheckingSetup(false);
      return;
    }

    await forgeGoalsService.ensureProfile(uid, '');
    const complete = await forgeGoalsService.isGoalsSetupComplete(uid);

    if (!complete) {
      const heroData = getProfileSetup(profileParam);
      if (heroData && heroData.goalTitle) {
        try {
          await forgeGoalsService.saveGoalsSetup(
            uid,
            {
              profileName: profileParam === 'theo' ? 'Theo' : 'Tracy',
              currentWeight: parseFloat(heroData.currentWeight) || 0,
              targetWeight: parseFloat(heroData.targetWeight) || 0,
              heightCm: parseFloat(heroData.heightCm) || 0,
              age: parseInt(heroData.age) || 0,
              gender: heroData.gender,
              activityLevel: heroData.activityLevel,
              targetCalories: parseInt(heroData.targetCalories) || 2000,
              targetProtein: parseInt(heroData.targetProtein) || 150,
              targetCarbs: parseInt(heroData.targetCarbs) || 200,
              targetFat: parseInt(heroData.targetFat) || 65,
              missionStartDate: new Date().toISOString().split('T')[0],
            },
            [
              {
                goalType: heroData.goalType,
                title: heroData.goalTitle,
                targetValue: heroData.goalTargetValue ? parseFloat(heroData.goalTargetValue) : undefined,
                targetUnit: heroData.goalType === 'weight_loss' ? 'kg' : '',
                startValue: heroData.currentWeight ? parseFloat(heroData.currentWeight) : undefined,
                targetDate: heroData.goalTargetDate || undefined,
                displayOrder: 1,
              },
            ]
          );
        } catch (err) {
          console.error('Auto-save from hero setup failed:', err);
          setShowOnboarding(true);
        }
      } else {
        setShowOnboarding(true);
      }
    }

    const badges = await forgeGoalsService.getUnseenBadges(uid);
    if (badges.length > 0) setUnseenBadge(badges[0]);
    const goals = await forgeGoalsService.getActiveGoals(uid);
    setActiveGoals(goals);
    setCheckingSetup(false);
  }, [profileParam]);

  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        setUserId(user.id);
        const name = user.user_metadata?.full_name || user.email?.split('@')[0] || 'Hunter';
        setProfileName(name);
        checkGoalsSetup(user.id);
      } else {
        setCheckingSetup(false);
      }
    });
  }, [checkGoalsSetup]);

  useEffect(() => {
    if (heroSetupMissing) {
      router.replace('/');
    }
  }, [heroSetupMissing, router]);

  async function handleOnboardingComplete(data: any) {
    if (!userId) return;
    setOnboardingLoading(true);
    try {
      await forgeGoalsService.saveGoalsSetup(userId, data, data.goals);
      setShowOnboarding(false);
      const goals = await forgeGoalsService.getActiveGoals(userId);
      setActiveGoals(goals);
    } catch (err) {
      console.error('Failed to save goals:', err);
    } finally {
      setOnboardingLoading(false);
    }
  }

  function handleBadgeClose() {
    if (userId && unseenBadge) {
      forgeGoalsService.markBadgesSeen(userId, [unseenBadge.id]);
    }
    setUnseenBadge(null);
  }

  function handleSetNextGoal() {
    if (userId && unseenBadge) {
      forgeGoalsService.markBadgesSeen(userId, [unseenBadge.id]);
    }
    setUnseenBadge(null);
    resetGoalCycle(profileParam);
    router.push('/');
  }

  if (checkingSetup || heroSetupMissing) {
    return (
      <AppLayout activeRoute="/profile-dashboard">
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center space-y-3">
            <svg className="animate-spin w-8 h-8 text-primary mx-auto" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <div className="text-sm text-muted-foreground">Loading your mission...</div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout activeRoute="/profile-dashboard">
      {showOnboarding && (
        <GoalOnboarding
          profileName={profileName}
          onComplete={handleOnboardingComplete}
          isLoading={onboardingLoading}
        />
      )}
      {unseenBadge && (
        <BadgeCelebration
          badge={unseenBadge}
          nextGoalTitle={activeGoals.find((g) => !g.isAchieved)?.title}
          onClose={handleBadgeClose}
          onSetNextGoal={handleSetNextGoal}
        />
      )}
      <div className="space-y-4 sm:space-y-6">
        <ProfileHeader />
        <div className="grid grid-cols-1 xl:grid-cols-3 2xl:grid-cols-3 gap-4 sm:gap-6">
          <div className="xl:col-span-2 space-y-4 sm:space-y-6">
            <BodyMetricsBento />
            <DailyMacros />
            <ActivityMetrics />
          </div>
          <div className="space-y-4 sm:space-y-6">
            <ForgeScorePanel />
            <WeightTrendChart />
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

export default function ProfileDashboardPage() {
  return (
    <Suspense fallback={
      <AppLayout activeRoute="/profile-dashboard">
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center space-y-3">
            <svg className="animate-spin w-8 h-8 text-primary mx-auto" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <div className="text-sm text-muted-foreground">Loading your mission...</div>
          </div>
        </div>
      </AppLayout>
    }>
      <ProfileDashboardInner />
    </Suspense>
  );
}